/**
 * The Season Report, on a screen.
 *
 * The DOM half of `systems/SeasonReport.ts`, and deliberately the thinner half:
 * everything this class knows about a season is a `SeasonReport`, which is a list
 * of labelled strings somebody else computed. It decides where the card sits, how
 * long it stays and when it goes away. It decides no number.
 *
 * That split is why the report is testable at all. `vitest.config.ts` runs the
 * suite in Node with no DOM, on purpose (ADR-010), so a card that built its own
 * arithmetic while writing `innerHTML` would be a card nothing could check.
 *
 * ## Why HTML and not the canvas
 *
 * `HelpOverlay` and `EndGameScreen` draw themselves with `fillText`, and both are
 * modal, short and fixed-length. This one is a table of up to thirty rows whose
 * length depends on the district, on a screen that is half a viewport tall — so it
 * needs wrapping, a scrollbar and a max height, which is three things a canvas
 * would have to reimplement. It goes in the same overlay layer as the status panel
 * and the news ticker, in the same municipal chrome.
 *
 * ## Why it is not modal
 *
 * A season is ninety budget cycles, which at the client's cadence is about fifteen
 * minutes of play. A card that stopped the game every fifteen minutes would make a
 * settlement an interruption; §12 asks for *one card*, and the rest of the design
 * asks for a district that keeps running whether or not anybody is watching it.
 *
 * So it appears in the corner, takes clicks only on itself, and leaves on its own
 * after `DWELL_MS`. A mayor who was looking somewhere else presses **R** and gets
 * it back — the record is in the Chronicle either way, and the card is a rendering
 * of a document rather than the document.
 *
 * See `docs/seasons.md` and `docs/ui-controls.md`.
 */

import type { ReportLine, SeasonReport } from '../systems/SeasonReport';

/** Where the card lives. Created by `index.html`, empty until a season closes. */
const HOST_ID = 'season-report';

export class SeasonReportCard {
  private host: HTMLElement | null = null;
  private report: SeasonReport | null = null;

  /**
   * Wall-clock milliseconds a card stays up on its own.
   *
   * Thirty seconds against a fifteen-minute season: long enough to read four
   * blocks, short enough that a player who has walked away comes back to the map
   * rather than to a panel. Dismissal is not a decision the game should make
   * *for* somebody who is reading, so the timer is paused while the pointer is
   * over the card.
   */
  private readonly DWELL_MS = 30_000;
  private dwell = 0;
  private held = false;

  /** Find the host element. Safe to call before it exists; retried on show. */
  init(): void {
    this.host = document.getElementById(HOST_ID);
    if (!this.host) return;
    this.host.addEventListener('pointerenter', () => { this.held = true; });
    this.host.addEventListener('pointerleave', () => { this.held = false; });
  }

  isVisible(): boolean {
    return this.report !== null;
  }

  /** The season currently on screen, or `null`. Lets **R** work as a toggle. */
  shownSeason(): number | null {
    return this.report?.season ?? null;
  }

  show(report: SeasonReport): void {
    if (!this.host) this.init();
    if (!this.host) return;

    this.report = report;
    this.dwell = 0;
    this.held = false;
    this.host.innerHTML = render(report);
    this.host.classList.remove('hidden');

    const close = this.host.querySelector('.season-report-close');
    close?.addEventListener('click', () => this.hide());
  }

  hide(): void {
    this.report = null;
    if (!this.host) return;
    this.host.classList.add('hidden');
    this.host.innerHTML = '';
  }

  /**
   * Age the card.
   *
   * Driven from `ParkingtonLayer.update(dt)` with the same accumulated `dt` the
   * autosave timer uses, for the same reason: it is a client effect of time
   * passing, and nothing about it may reach the simulation. A card that outlived
   * its welcome would still not have changed a single total.
   */
  update(dt: number): void {
    if (!this.report || this.held) return;
    this.dwell += dt;
    if (this.dwell >= this.DWELL_MS) this.hide();
  }
}

/** Escape anything that came from a record. Labels are ours; values are counted. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function renderLine(line: ReportLine): string {
  const tone = line.tone && line.tone !== 'plain' ? ` season-report-${line.tone}` : '';
  const note = line.note
    ? ` <span class="season-report-note">(${escapeHtml(line.note)})</span>`
    : '';
  return (
    `<div class="season-report-line">` +
    `<span class="season-report-label">${escapeHtml(line.label)}</span>` +
    `<span class="season-report-value${tone}">${escapeHtml(line.value)}${note}</span>` +
    `</div>`
  );
}

function render(report: SeasonReport): string {
  let html =
    `<div class="season-report-head">` +
    `<div class="season-report-title">SEASON ${report.season} REPORT</div>` +
    `<div class="season-report-span">${escapeHtml(report.span)}</div>` +
    `<button class="season-report-close" aria-label="Dismiss report">×</button>` +
    `</div>`;

  html += `<div class="season-report-body">`;
  for (const block of report.blocks) {
    html += `<div class="season-report-block">`;
    html += `<div class="season-report-block-title">${escapeHtml(block.title)}</div>`;
    for (const line of block.lines) html += renderLine(line);
    html += `</div>`;
  }
  html += `</div>`;

  html += `<div class="season-report-footer">${escapeHtml(report.footer)}</div>`;
  return html;
}
